import * as logger from "../../utils/logger";
import DriveApi from "../../utils/api";
import RollingLimiter from "../../utils/limiter";
import { calculateCoordinateSpeed, Mutable } from "../../utils/helpers";
import { getServerDate } from "../../utils/storage/date";
import { FeatureFlagName } from "../../utils/featureFlagName";
import { getCurrentPosition } from "../../utils/location";
import { shouldAddExtraLogging } from "../../utils/logger.internal";
import { extend } from "../../utils/vanilla";
import { DriveStore } from "../../store/reducers";
import { MobileActions } from "../../store/mobile/actions";
import { observeStore } from "../../utils/store";
import Context from "../../app.context";
import { isInCanada } from "hos-tools";
import { IFeatureFlagsService } from "../featureFlags";
import constants from "../../constants";
import { DataActions } from "../../store/data/actions";
import { defaultData } from "../../store/data/reducers";
import { NetworkStates } from "../../store/mobile/types";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import toastr from "../../utils/toastr";

interface IGeolocationMock extends Geolocation {
    mock?: any;
}

/**
 * Single instance of this class used in app.manager.ts
 * Service is responsible for tracking the vehicle's movement based on phone's GPS data
 */
export default class Movement {

    /**
     * Set by app.manager.js on switching users
     */
    api: DriveApi | null = null;

    /**
     * Made public to allow this property to be set to a "mockup" geolocation
     */
    geolocation: IGeolocationMock = navigator.geolocation;
    private readonly gpsAccuracyRecords: number[] = [];
    private readonly movementKey = "movement";
    private readonly ioxAttachedGPSStopTrackingMessage = "GPS: Stopped tracking due to IOX cable attachment";
    private watchId = -1;
    private silenceTimeout: any = null;
    private setDriveTimeout: any = null;
    private backupWatchPositionTimeout: any = null;
    private stopTrackingWaitTimeout: any = null;
    private hasShownPermissionDeniedWarning = false;
    private readonly defaultOptions: PositionOptions = {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
    };
    private readonly driveTimeoutDelay: number = constants.MOVEMENT.DEFAULT_DRIVE_TIMEOUT_DELAY;
    private drivingThreshold: number = constants.MOVEMENT.DEFAULT_DRIVING_THRESHOLD;

    /**
      * 2 minute GPS position log
      */
    private readonly positions = new RollingLimiter<GeolocationPosition>({ clearingInterval: 2 * 60 * 1000 });

    constructor(
        private readonly store: DriveStore,
        private readonly featureFlagsService: IFeatureFlagsService,
        driveDelay?: number
    ) {
        if (driveDelay !== undefined) {
            this.driveTimeoutDelay = driveDelay;
        }

        // When status changes, restart the tracking. Clear the stop tracking wait timeout
        observeStore(this.store, state => state.mobile.charging || false, this.trackMovement.bind(this));
        observeStore(this.store, state => state.mobile.ioxAttached || false, this.updateTrackingAfterIOXChange.bind(this));
    }

    /**
     * Change the API in this class and also update drivingThreshold depending on user & database's configuration
     * @param context Driver's context (temporary context on login page)
     */
    changeMovementContext(context: Context) {
        this.api = context.api.driveApi;

        // Check for CAN ruleset
        if (context.hosProcess && isInCanada(context.hosProcess.getLatestHosRuleSetIdShortened())) {
            this.drivingThreshold = this.featureFlagsService.getFeatureFlagValue(FeatureFlagName.LOCKSCREEN_STOPPING_THRESHOLD, constants.MOVEMENT.STOPPING_THRESHOLD) as number; //km/h
        } else {
            this.drivingThreshold = constants.MOVEMENT.DEFAULT_DRIVING_THRESHOLD; //km/h
        }
    }

    getLast5SecondAverage(): number {
        let date = getServerDate().getTime();
        let sum = 0;
        let elementCount = 0;
        let array = this.positions.getData(this.movementKey);

        if (array.length === 0) {
            return 0;
        }

        let i = array.length - 1;
        while (i >= 0 && date - array[i].recordDate.getTime() <= constants.MOVEMENT.GPS_VALID_TIME_LIMIT) {
            let item = array[i];
            let speed = item.data.coords.speed;
            if (speed) {
                sum += speed;
            }
            elementCount += 1;
            i -= 1;
        }

        return sum / elementCount;
    }

    trackMovement() {
        if (!this.geolocation) {
            return;
        }

        window.clearTimeout(this.stopTrackingWaitTimeout);

        let user = this.api ? this.api.getUser() : null;
        if (this.isWatchAllowed()) {
            logger.log(logger.Source.GEO, "GPS: Start tracking", user);
            this.startTracking(user);
        } else {
            if (this.store.getState().mobile.ioxAttached) {
                this.stopTracking(this.ioxAttachedGPSStopTrackingMessage);
            } else {
                // If not charging, set a timer of ~1 minute before turning off the GPS - in case trip has ended and power lost
                this.stopTrackingWaitTimeout = setTimeout(() => {
                logger.log(logger.Source.GEO, `GPS: Stop tracking after ${ (constants.MOVEMENT.STOP_TRACKING_WAIT_INTERVAL / 1000) } seconds`, user);
                this.stopTracking();
                }, constants.MOVEMENT.STOP_TRACKING_WAIT_INTERVAL);
            }
        }
    }

    isPositionAccurate(position: GeolocationPosition): boolean {
        // If the position's accuracy is worse than the allowed maximum, always reject it.
        if (position.coords.accuracy > constants.MOVEMENT.MAX_ACCURACY) {
            return false;
        }

        // If we have calculated an average accuracy before..
        let averageAccuracy = this.store.getState().data.gpsAccuracyAverage;
        if (averageAccuracy !== defaultData.gpsAccuracyAverage) {
            // Check against the average accuracy (within the factor)
            let accuracyFactor = constants.MOVEMENT.GET_AVERAGE_ACCURACY_FACTOR(averageAccuracy);
            if (position.coords.accuracy > averageAccuracy * accuracyFactor) {
                return false;
            }
        }

        return true;
    }

    /**
     * Begin tracking the vehicle/tablet's movement
     */
    startTracking(user: IUser | null) {
        let deviceProvidedSpeed = false;

        let failure = (error: GeolocationPositionError) => {
            window.clearTimeout(this.backupWatchPositionTimeout);
            if (error?.code === 1 /* PERMISSION_DENIED */ && !this.hasShownPermissionDeniedWarning) {
                this.hasShownPermissionDeniedWarning = true;
                toastr.warning("Location access was denied. Please enable location permissions in your browser or device settings.", undefined, undefined, { preventDuplicates: true });
            }
        };

        let success = (position: GeolocationPosition) => {
            if (this.hasShownPermissionDeniedWarning) {
                this.hasShownPermissionDeniedWarning = false;
            }
            this.gpsAccuracyRecords.push(position.coords.accuracy);

            // Calculate average GPS accuracy once we've received the minimum number of samples.
            if (this.gpsAccuracyRecords.length >= constants.MOVEMENT.AVERAGE_ACCURACY_MINIMUM_SAMPLES) {

                // If we've hit the maximum samples, remove the oldest record
                if (this.gpsAccuracyRecords.length > constants.MOVEMENT.AVERAGE_ACCURACY_MAXIMUM_SAMPLES) {
                    this.gpsAccuracyRecords.shift();
                }

                let averageAccuracy = this.gpsAccuracyRecords.reduce((a, b) => a + b, 0) / this.gpsAccuracyRecords.length;

                // Only update the average if the delta is more than 1 meter different to avoid excessive store updates
                if (Math.abs(averageAccuracy - this.store.getState().data.gpsAccuracyAverage) >= 1) {
                    this.store.dispatch(DataActions.setGpsAccuracyAverage(averageAccuracy));
                }
            }

            let positionCopy = {
                timestamp: position.timestamp,
                coords: extend(position.coords, {}) as Mutable<GeolocationCoordinates>
            };

            Object.defineProperty(positionCopy.coords, "speed", {
                writable: true,
                value: positionCopy.coords.speed
            });
            let isDriving = this.store.getState().data.driving;
            let speed = positionCopy.coords.speed;
            let calculatedSpeed = 0;
            let kmph = this.mpsToKmph(speed);
            let lastPosition = this.positions.getLast(this.movementKey);
            let speedWasNull = false;
            let avgKmph;

            window.clearTimeout(this.backupWatchPositionTimeout);
            this.backupWatchPositionTimeout = setTimeout(failure, (this.defaultOptions.timeout || 5000) + 500, { code: 3, message: "Backup PositionError. Timeout after 6 seconds" });

            if (speed === null) {
                speedWasNull = true;

                // This browser/phone doesn't support speed in geolocation objects. Calculate it by ourselves!
                if (lastPosition) {
                    calculatedSpeed = calculateCoordinateSpeed(
                        lastPosition.data.coords.latitude,
                        lastPosition.data.coords.longitude,
                        lastPosition.data.timestamp,
                        position.coords.latitude,
                        position.coords.longitude,
                        position.timestamp
                    );
                }

                positionCopy.coords.speed = calculatedSpeed;
                kmph = this.mpsToKmph(calculatedSpeed);

                // Device has previously provided speed, don't fallback on calculated speed.
                // Still perform calculation so we can see what values of speed it was giving.
                if (deviceProvidedSpeed) {
                    logger.log(logger.Source.GEO, `Null speed ignored. ${ this.getPositionOutput(positionCopy) }`);
                    return;
                }
            } else {
                deviceProvidedSpeed = true;
            }

            // Logging for if position.timestamp outside of 5min from system date time. Don't skip yet, but just additional logs
            if ((Math.abs(getServerDate().getTime() - positionCopy.timestamp) > 300000)) {
                logger.log(logger.Source.GEO, `Date offset - position.timestamp: ${ new Date(positionCopy.timestamp).toISOString() }`, user);
            }

            // Only read position with valid accuracy and a "new" position. On some devices with bad GPS
            // they send old/repeated position messages. Additionally, exclude spikes and poor accuracy
            if (!(this.isPositionAccurate(positionCopy)) || (lastPosition && positionCopy.timestamp <= lastPosition.data.timestamp) || kmph > 200) {
                logger.log(
                    logger.Source.GEO,
                    `Ignored. Position ${ this.getPositionOutput(positionCopy) } - Average Accuracy: ${ this.store.getState().data.gpsAccuracyAverage } (from ${ this.gpsAccuracyRecords.length } records)`,
                    user
                );
                return;
            }

            this.positions.add(this.movementKey, positionCopy);
            avgKmph = this.mpsToKmph(this.getLast5SecondAverage());

            // This is a very noisy log that could fill up logger queue, so require explicit enabling of extra logging for it (ie. `%|GEO|%`)
            if (shouldAddExtraLogging(logger.Source.GEO, undefined, true, user)) {
                logger.log(
                    logger.Source.GEO,
                    `${ this.getPositionOutput(positionCopy) } avg (5s) => ${ avgKmph.toFixed(0) }km/h ${ (speedWasNull ? "(calculated speed)" : "") } - Average Accuracy: ${ this.store.getState().data.gpsAccuracyAverage } (from ${ this.gpsAccuracyRecords.length } records)`,
                    user
                );
            }
            let isConnected = !this.store.getState().mobile.gpsConnected ? true : this.store.getState().mobile.gpsConnected;
            this.store.dispatch(MobileActions.setGps({ isConnected: isConnected, position: position }));
            let dateTime: string = new Date(position.timestamp).toISOString();
            const speedKmh = this.mpsToKmph(positionCopy["coords"]["speed"]);
            dispatcher.trigger(DispatcherEvents.NewPositionDataForLockScreen, { latitude: positionCopy["coords"]["latitude"], longitude: positionCopy["coords"]["longitude"], dateTime: dateTime,
                speed: speedKmh, source: "Web"});

            if (avgKmph > constants.MOVEMENT.STOPPING_THRESHOLD) {
                this.extendSilenceTimeout();
            }

            if (avgKmph >= this.drivingThreshold) {
                if (isDriving !== true && this.setDriveTimeout === null) {
                    this.setDriveTimeout = setTimeout(() => {
                        let averageKmph = this.mpsToKmph(this.getLast5SecondAverage());
                        dispatcher.trigger(DispatcherEvents.NewLastFiveSecondAvgGpsData, {
                            lastFiveSecondAvgGpsSpeed: averageKmph,
                            checkingVehicleStopped: false,
                            user,
                            dateTime: getServerDate().toISOString()
                        });

                        this.setDriveTimeout = null;
                    }, this.driveTimeoutDelay);
                }
            } else {
                window.clearTimeout(this.setDriveTimeout);
                this.setDriveTimeout = null;
            }
        };

        this.geolocation.clearWatch(this.watchId);
        this.watchId = this.geolocation.watchPosition(success, failure, {
            ...this.defaultOptions,
            ...{
                enableHighAccuracy: true,
                maximumAge: 0
            }
        });
    }

    /**
     * Stop tracking the motion.
     */
    stopTracking(message?: string) {
        window.clearTimeout(this.stopTrackingWaitTimeout);
        window.clearTimeout(this.silenceTimeout);
        window.clearTimeout(this.setDriveTimeout);
        window.clearTimeout(this.backupWatchPositionTimeout);
        this.geolocation.clearWatch(this.watchId);
        if (message) {
            let user = this.api ? this.api.getUser() : null;
            logger.log(logger.Source.GEO, message, user);
        }
    }

    updateTrackingAfterIOXChange() {
        this.store.getState().mobile.ioxAttached ? this.stopTracking(this.ioxAttachedGPSStopTrackingMessage) : this.trackMovement();
    }

    ensureWatchPosition() {
        let lastPosition = this.positions.getLast(this.movementKey);
        let ageOfLastPosition = Infinity;
        let startAgain = false;
        let originalGpsConnectedStatus = this.store.getState().mobile.gpsConnected;

        if (!this.isWatchAllowed()) {
            return;
        }

        if (lastPosition) {
            let positionDate = lastPosition.recordDate.getTime();
            ageOfLastPosition = getServerDate().getTime() - positionDate;
            if (ageOfLastPosition > constants.MOVEMENT.ENSURE_WATCH_POSITION_TIME) {
                startAgain = true;
            }
        } else {
            startAgain = true;
        }

        if (startAgain) {
            // Found a bug - if lastPosition was -1, null, or undefined it would get added to app state.
            // Also needed "item" property, not the lastPosition object we made
            this.store.dispatch(MobileActions.setGps({ isConnected: false, position: lastPosition ? lastPosition.data : undefined }));
            if (originalGpsConnectedStatus) {
                logger.event(
                    logger.Event.RESTARTTRACKING,
                    ageOfLastPosition === Infinity ? "No position recorded" : `Last position ${ ageOfLastPosition / 1000 }s old`,
                    this.api ? this.api.getUser() : null
                );
            }
            this.trackMovement();
        }
    }

    /**
     * Method to be scheduled that will check current position + speed
     * and then dispatch updates to the app regarding GPS and driving status
     */
    monitorPosition() {
        let validatePosition = (pos: GeolocationPosition): boolean => {
            let isAccurate = pos.coords.accuracy <= constants.MOVEMENT.MAX_ACCURACY;
            let isSpeedValid = (pos.coords.speed !== null && this.mpsToKmph(pos.coords.speed) < 200);
            let isRecent = Math.abs(getServerDate().getTime() - pos.timestamp) < 300000;
            return isAccurate && isSpeedValid && isRecent;
        };

        return getCurrentPosition(this.store).then(position => {
            let isValid = validatePosition(position);

            // If network down, set driving state based on GPS result
            if (isValid && this.store.getState().mobile.online !== NetworkStates.ONLINE) {
                let kmph = this.mpsToKmph(position.coords.speed);
                dispatcher.trigger(DispatcherEvents.NewGpsData, { speed: kmph, dateTime: getServerDate().toISOString() });
            }
        }).catch((err?: Error) => {
            err && logger.log(logger.Source.SERVICES, err.message);
        });
    }

    containRecentGpsRecord() {
        let array = this.positions.getData(this.movementKey);
        let date = getServerDate().getTime();
        let length = array.length;

        return length > 0 && date - array[length - 1].recordDate.getTime() <= constants.MOVEMENT.GPS_VALID_TIME_LIMIT;
    }

    /**
      * Refreshes/extends the timeout action after GPS silence for SILENCE_DURATION seconds
      */
    extendSilenceTimeout() {
        let user = this.api ? this.api.getUser() : null;
        window.clearTimeout(this.silenceTimeout);

        this.silenceTimeout = setTimeout(() => {
            // check if we have new data OR if gps is connected
            // eslint-disable-next-line max-len
            if (!(this.containRecentGpsRecord()) || !this.store.getState().mobile.gpsConnected) {
                // GPS not reported in watchPosition timeout. Either spotty gps while traveling, or into garage/tunnel
                logger.event(logger.Event.MOTIONCHANGE, "Source: GPS. Undefined. Spotty GPS?", user);
                this.store.dispatch(DataActions.setDriving(undefined));

                let message = `GPS: Signal lost or no gps records within the last 5 seconds. Undefined driving. gpsConnected = ${ this.store.getState().mobile.gpsConnected }`;
                logger.log(logger.Source.GEO, message, user);

                this.extendSilenceTimeout();
                return;
            }

            let fiveSecondAverage = this.mpsToKmph(this.getLast5SecondAverage());
            dispatcher.trigger(DispatcherEvents.NewLastFiveSecondAvgGpsData, {
                lastFiveSecondAvgGpsSpeed: fiveSecondAverage,
                checkingVehicleStopped: true,
                user,
                dateTime: getServerDate().toISOString()
            });
        }, constants.MOVEMENT.SILENCE_DURATION);
    }
    private mpsToKmph(mps: number | null) {
        return (mps || 0) * 3.6;
    }

    /**
     * Track geolocation when plugged in, when battery is above 90%, GPS driver assignment custom code is enabled or the user is on the login screen to alert user to stop driving without authenticating
     */
    private isWatchAllowed(): boolean {
        if (this.store.getState().mobile.ioxAttached) {
            return false;
        }
        let batteryLevel = this.store.getState().mobile.batteryLevel;
        let charging = this.store.getState().mobile.charging;
        return (typeof (charging) === "undefined" || charging) || (batteryLevel && batteryLevel > 90)
            || (this.store.getState().mobile.currentPage?.indexOf("login") !== -1 && !this.store.getState().mobile.background);
    }

    private getPositionOutput(position: GeolocationPosition): string {
        let speed = `Speed: ${ this.mpsToKmph(position.coords.speed) }km/h`;
        let coords = `Coords: [${ position.coords.latitude },${ position.coords.longitude }]`;
        let accuracy = `Accuracy: ${ position.coords.accuracy }`;
        let timeStamp = `Timestamp: ${ new Date(position.timestamp).toISOString() }`;
        let heading = `Heading: ${ position.coords.heading }`;
        return `{${ speed }, ${ coords }, ${ accuracy }, ${ timeStamp }, ${ heading }}`;
    }
}